import { useEffect, useMemo } from 'react'
import { fetchRedirects, saveRedirects } from '../adminApi'
import { useAdmin } from '../AdminContext'
import { useServerDraft } from '../../hooks/useServerDraft'
import { knownSitePaths, redirectIssues } from '../../core/rules/redirects'
import { plural } from '../../core/format/text'
import type { Redirect, RedirectsResponse } from '../../core/types/seo'

const toDraft = (r: RedirectsResponse): Redirect[] => r.items
const persist = (items: Redirect[], r: RedirectsResponse) => saveRedirects({ revision: r.revision, items })

/**
 * Doorverwijzingen: laden, lokaal bewerken en als één lijst opslaan (PUT /admin/redirects).
 * Gaat mee met de Opslaan-balk van het beheer (registerSave); de server krijgt wel een eigen verzoek.
 * Hangt aan de pagina Instellingen, niet aan het onderwerp: wisselen van onderwerp verliest niets.
 * Waarschuwingen komen uit core/rules/redirects.ts; de server controleert hetzelfde (422).
 */
export function useRedirects() {
  const { catalog, registerSave } = useAdmin()
  const s = useServerDraft({
    load: fetchRedirects,
    toDraft,
    persist,
    savedTitle: 'Doorverwijzingen opgeslagen',
    savedText: 'Oude links gaan nu naar de nieuwe pagina.',
    conflictSubject: 'De doorverwijzingen',
  })

  const current = s.current
  const items = useMemo(() => current ?? [], [current])
  const paths = useMemo(() => knownSitePaths(catalog), [catalog])
  const issues = useMemo(() => redirectIssues(items, (p) => paths.has(p)), [items, paths])
  const errors = useMemo(() => issues.reduce((n, list) => n + list.filter((i) => i.level === 'error').length, 0), [issues])

  const { dirty, saving, save, discard } = s
  const blockedBy = errors > 0 ? `De doorverwijzingen hebben nog ${plural(errors, 'fout', 'fouten')} (Instellingen › Vindbaarheid & pers).` : null
  useEffect(() => {
    registerSave('redirects', { dirty, saving, blockedBy, label: 'De doorverwijzingen', save, discard })
  }, [registerSave, dirty, saving, blockedBy, save, discard])
  useEffect(() => () => registerSave('redirects', null), [registerSave])

  return { data: s.data, items, issues, errors, status: s.status, reload: s.reload, dirty, edit: s.edit, paths }
}

export type RedirectsState = ReturnType<typeof useRedirects>
